import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InstructorEntity } from '@core/entities';
import { DefaultImageDirective } from '@shared/directives';
import { AlertType, NotificationType, SocketEvents } from '@core/enums';
import { untilDestroyed } from '@ngneat/until-destroy';
import { AlertMessageComponent, DynamicTabsComponent, IconComponent, TabDirective } from '@shared/components/ui';
import { InstructorsComposition } from '@shared/compositions';
import { DateTimeUtility, getAlertTypeByApprovedStatusOfEntity, isMobileView } from '@core/utils';
import { DialogRef } from '@ngneat/dialog';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { InstructorFormComponent } from '@shared/components/forms';

@Component({
  selector: 'app-course-instructor',
  imports: [CommonModule, IconComponent, DynamicTabsComponent, TranslateModule, TabDirective, NgxSkeletonLoaderModule, DefaultImageDirective, AlertMessageComponent],
  templateUrl: './course-instructor.component.html',
  styleUrls: ['./course-instructor.component.scss'],
})
export class CourseInstructorComponent extends InstructorsComposition implements OnInit {
  @Input() instructor: InstructorEntity;
  @Input() instructorId: number;
  @Input() showHeader = true;
  protected readonly isMobileView = isMobileView;

  constructor() {
    super();
    if (!isMobileView()) {
      this._dialogRef = inject(DialogRef);
    }
    this.isLoading = true;
  }

  get alertType(): AlertType {
    return getAlertTypeByApprovedStatusOfEntity(this.currentInstructor);
  }

  get instructorAvailability(): { name: string; time: string }[] {
    const availability = this.currentInstructor.availability;
    return availability.map((item) => {
      const weekdayNumber = isNaN(+item.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(item.weekday) : +item.weekday;
      return {
        name: this.days.find((day) => day.number === +weekdayNumber)?.name,
        time: `${item.timeFrom} - ${item.timeTo}`,
      };
    });
  }

  get instructorQualifications(): string[] {
    return this.currentInstructor.qualification.map((item) => item.qualification);
  }

  get instructorBookingInOtherCourses(): string[] {
    const courseNames = this.currentInstructor.instructorBookings.map((item) => item.course.name);
    return courseNames.filter((name, index) => courseNames.indexOf(name) === index);
  }

  close() {
    if (isMobileView()) {
      this._bottomSheet.close();
    } else {
      this._dialogRef.close();
    }
  }

  ngOnInit() {
    this.instructor = this.instructor || (isMobileView() ? this._bottomSheet?.currentSheetData?.instructor : this._dialogRef.data.instructor);
    this.instructorId = this.instructor?.id || (isMobileView() ? this._bottomSheet?.currentSheetData?.instructorId : this._dialogRef.data.instructorId);
    this._fetchInstructor();

    this._socketService
      .fromEvent(SocketEvents.GetNotification)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (data: NotificationType) => {
          if (data === NotificationType.Instructor) {
            this._fetchInstructor();
          }
        },
      });
  }

  editInstructor() {
    if (!isMobileView()) {
      this._dialogService.open(InstructorFormComponent, {
        size: 'md',
        closeButton: false,
        windowClass: 'dialog-overflow-y-auto',
        data: {
          instructor: this.currentInstructor,
        },
      });
    } else {
      this._bottomSheet.open(InstructorFormComponent, {
        height: '95%',
        showCloseButton: false,
        data: {
          instructor: this.currentInstructor,
        },
      });
    }
  }

  private _fetchInstructor() {
    if (this.instructor && this.instructor.id) {
      this.currentInstructor = this.instructor;
      this.instructorId = this.instructor.id;
    }
    this._fetchInstructorById(this.instructorId);
  }
}
